import { Component, inject, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageModule } from 'primeng/message';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { MagentoInstance } from '../../core/models';

@Component({
    selector: 'app-instances',
    standalone: true,
    imports: [FormsModule, TableModule, CardModule, TagModule, ToggleSwitchModule, MessageModule],
    template: `
        <p-card header="Magento Instances">
            <p-message severity="info" styleClass="mb-3">
                Only one instance is active at a time — enabling one disables the others. All Magento order syncs and acknowledgements use the active instance.
            </p-message>

            <p-table [value]="instances()" [loading]="loading()" [paginator]="instances().length > 10" [rows]="10">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Name</th>
                        <th>Base URL</th>
                        <th>Status</th>
                        <th>Active</th>
                        <th>Installation Hash</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>{{ row.name }}</td>
                        <td>{{ row.baseUrl }}</td>
                        <td><p-tag [value]="row.isEnabled ? 'Enabled' : 'Disabled'" [severity]="row.isEnabled ? 'success' : 'secondary'" /></td>
                        <td>
                            <p-toggleswitch
                                [ngModel]="row.isEnabled"
                                [disabled]="busy()"
                                (onChange)="toggle(row, $event.checked)" />
                        </td>
                        <td>{{ row.installationHash }}</td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="5">{{ error() ?? 'No Magento instances found.' }}</td>
                    </tr>
                </ng-template>
            </p-table>

            @if (error() && instances().length) {
                <p-message severity="error" styleClass="mt-3">{{ error() }}</p-message>
            }
        </p-card>
    `
})
export class Instances implements OnInit {
    private readonly api = inject(MiddlewareApi);
    readonly instances = signal<MagentoInstance[]>([]);
    readonly loading = signal(true);
    readonly busy = signal(false);
    readonly error = signal<string | null>(null);

    ngOnInit(): void {
        this.reload();
    }

    toggle(row: MagentoInstance, enabled: boolean): void {
        const id = this.idOf(row);
        if (!id) return;
        this.busy.set(true);
        this.error.set(null);
        this.api.setInstanceEnabled(id, enabled).subscribe({
            // Refetch so single-active changes (the other rows flipping off) show immediately.
            next: () => this.reload(),
            error: (e) => {
                this.error.set(this.describe(e));
                this.busy.set(false);
                this.reload(); // revert the optimistic switch back to server truth
            }
        });
    }

    private reload(): void {
        this.loading.set(true);
        this.api.getInstances().subscribe({
            next: (d) => {
                this.instances.set(d ?? []);
                this.loading.set(false);
                this.busy.set(false);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.loading.set(false);
                this.busy.set(false);
            }
        });
    }

    private idOf(row: MagentoInstance): string | null {
        if (!row.id) return null;
        return typeof row.id === 'string' ? row.id : row.id.value;
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API (is it running, and is CORS allowed?).';
        if (e?.status === 401) return 'Unauthorized — sign in first.';
        if (e?.status === 403) return 'Forbidden — your role lacks the instances:manage permission.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
